// Infers what a column actually holds — numeric, date, or text — so the column
// menu can label sorting correctly (AC-15842). No column declares its type, and
// hand-annotating every numeric/date column across ~30 tables would not stay
// correct as columns change, so infer from the values.
//
// A column can always override the wording by setting `sortLabels: { asc, desc }`,
// which <ColumnMenu> prefers over anything inferred here.
//
// IMPORTANT: this only decides *wording*. The ordering itself comes from
// smartSortType, which classifies each compared pair independently. Keep the two
// consistent — a label claiming an ordering the comparator won't deliver is
// worse than the neutral A → Z wording (that was the bug in the reverted first
// attempt at AC-15842).

export const TEXT_SORT_LABELS = { asc: "Sort A → Z", desc: "Sort Z → A" };
export const NUMERIC_SORT_LABELS = {
  asc: "Sort Smallest to Largest",
  desc: "Sort Largest to Smallest",
};
export const DATE_SORT_LABELS = {
  asc: "Sort Oldest to Newest",
  desc: "Sort Newest to Oldest",
};

// How many rows to look at. Enough to skip leading blanks without walking a
// full client-side table every time the labels are recomputed.
const SAMPLE_SIZE = 20;

// Plain integer/decimal only. Deliberately excludes things that merely contain
// digits (IDs with separators, "1.2.3" versions, "50%") — misclassifying text
// as numeric is worse than falling back to A → Z.
const NUMERIC_RE = /^-?\d+(\.\d+)?$/;

// ISO-8601-ish or common M/D/Y. Deliberately narrow: a bare "5" parses as a date
// in some engines, so Date.parse is never trusted on its own.
const DATE_RE = /^\d{4}-\d{2}-\d{2}([T ]|$)|^\d{1,2}\/\d{1,2}\/\d{2,4}$/;

export const isBlank = (v) => v === null || v === undefined || v === "";

/**
 * Classify a single value. Returns "numeric" | "date" | "text", or null when the
 * value tells us nothing (blank, or a type we don't reason about).
 */
export function kindOf(value) {
  if (typeof value === "number") return Number.isFinite(value) ? "numeric" : null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : "date";
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (trimmed === "") return null;
  if (DATE_RE.test(trimmed) && !Number.isNaN(Date.parse(trimmed))) return "date";
  if (NUMERIC_RE.test(trimmed)) return "numeric";
  return "text";
}

/**
 * Resolve a column's type from resolved react-table rows.
 *
 * Requires every non-blank sampled value to agree before committing to numeric
 * or date; a single text value drops the column back to text. Accessor-less
 * columns have no values at all, so they also fall back.
 *
 * @param {Array} rows react-table row objects (each with `.values`). Pass an
 *   instance-level collection such as `preFilteredRows` — NOT a column's own
 *   `preFilteredRows`, which react-table drops once that column has an active
 *   filter and which holds only the current page under manualPagination.
 * @param {string} columnId
 * @returns {"numeric"|"date"|"text"}
 */
export default function columnTypeOf(rows, columnId) {
  if (!Array.isArray(rows) || rows.length === 0) return "text";

  let resolved = null;
  let seen = 0;

  for (const row of rows) {
    if (seen >= SAMPLE_SIZE) break;
    const value = row?.values?.[columnId];
    if (isBlank(value)) continue;

    seen += 1;
    const kind = kindOf(value);
    if (kind === null) continue;
    if (kind === "text") return "text";
    if (resolved === null) resolved = kind;
    else if (resolved !== kind) return "text";
  }

  return resolved ?? "text";
}

/** Sort-direction wording for a resolved column type. */
export function sortLabelsForType(type) {
  if (type === "numeric") return NUMERIC_SORT_LABELS;
  if (type === "date") return DATE_SORT_LABELS;
  return TEXT_SORT_LABELS;
}
